import { Component, OnInit, inject, computed, input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { DatePickerComponent, DateRangePickerOutput, TimeUnit } from '../../../../shared/components/date-picker/date-picker.component';
// import { BusinessIndustriesStatsComponent } from "./business-industries-stats/business-industries-stats.component";
import { BusinessStateStatsComponent } from "./business-state-stats/business-state-stats.component";
import { BusinessStagePhaseStatsComponent } from "./business-stage-phase-stats/business-stage-phase-stats.component";
import { BusinessHighlightsComponent } from "./business-highlights/business-highlights.component";
import { BusinessStatewiseStatsComponent } from "./business-statewise-stats/business-statewise-stats.component";
import { ActiveBusinessBarsComponent } from "./active-business-bars/active-business-bars.component";
import { BusinessCohortGroupComponent } from "./business-cohort-group/business-cohort-group.component";
import { BusinessesRegisteredStatsComponent } from "./businesses-registered-stats/businesses-registered-stats.component";
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { signal, effect, untracked } from '@angular/core';
import { DropdownComponent, DropdownOption } from "../../../../shared/components/ui/dropdown/dropdown.component";
import { CURRENCY_DROPDOWN_OPTIONS, getCurrencyInfoByCocuId } from '../../../../shared/utils/country-currency-options.util';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DashboardPageKey } from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  createEmbeddedSectionFilterByWidgetKeyMap,
  findCatalogPageId,
  resolveActiveCatalogProductId,
} from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

/**
 * Section ids for the customizable Business Performance Report dashboard.
 * Defined locally so adding the Customize Layout UI here does NOT touch
 * any shared enum / catalog files (purely additive change).
 *
 * NOTE: The four `BUSINESS_STATE_*` ids represent the individual cards
 * rendered inside the original `business-state-stats` component (Active /
 * Inactive / Activated / Revived). They are listed separately here so each
 * card appears as its own entry in the Customize Layout popup, matching
 * the 12 visible cards on screen 1:1.
 */
export enum BusinessPerformanceReportSectionId {
  BUSINESSES_REGISTERED       = 'businesses_registered',
  BUSINESS_STATE_ACTIVE       = 'business_state_active',
  BUSINESS_STATE_INACTIVE     = 'business_state_inactive',
  BUSINESS_STATE_ACTIVATED    = 'business_state_activated',
  BUSINESS_STATE_REVIVED      = 'business_state_revived',
  BUSINESS_STAGE              = 'business_stage',
  BUSINESS_HIGHLIGHTS         = 'business_highlights',
  TRENDING_INDUSTRIES         = 'trending_industries',
  BUSINESS_STATEWISE          = 'business_statewise',
  BUSINESS_PHASE              = 'business_phase',
  ACTIVE_BUSINESSES           = 'active_businesses',
  BUSINESS_COHORT_GROUP       = 'business_cohort_group',
}

interface BusinessPerformanceReportSectionDef {
  id: BusinessPerformanceReportSectionId;
  title: string;
}

const BUSINESS_PERFORMANCE_REPORT_SECTION_DEF: BusinessPerformanceReportSectionDef[] = [
  { id: BusinessPerformanceReportSectionId.BUSINESSES_REGISTERED,    title: 'Businesses Registered Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVE,    title: 'Active Business' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_INACTIVE,  title: 'Inactive Business' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVATED, title: 'Business Activated' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATE_REVIVED,   title: 'Business Revived' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STAGE,           title: 'Business Stage Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_HIGHLIGHTS,      title: 'Business Highlights' },
  { id: BusinessPerformanceReportSectionId.TRENDING_INDUSTRIES,      title: 'Trending Industries' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_STATEWISE,       title: 'Business Statewise Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_PHASE,           title: 'Business Phase Stats' },
  { id: BusinessPerformanceReportSectionId.ACTIVE_BUSINESSES,        title: 'Active Businesses Stats' },
  { id: BusinessPerformanceReportSectionId.BUSINESS_COHORT_GROUP,    title: 'Business Cohort Group' },
];

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: BusinessPerformanceReportSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

@Component({
  selector: 'app-business-performance-report',
  imports: [
    DatePickerComponent,
    // BusinessIndustriesStatsComponent,
    BusinessStateStatsComponent,
    BusinessStagePhaseStatsComponent,
    BusinessHighlightsComponent,
    BusinessStatewiseStatsComponent,
    ActiveBusinessBarsComponent,
    BusinessCohortGroupComponent,
    BusinessesRegisteredStatsComponent,
    DropdownComponent,
    FormsModule,
    CommonModule,
    AiHeadlinesComponent,
    DragDropModule,
    LayoutEditorOverlayDirective,
    IconComponentV2,
],
  templateUrl: './business-performance-report.component.html',
  styleUrl: './business-performance-report.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class BusinessPerformanceReportComponent implements OnInit {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  /**
   * Catalog `pages.id` for this report — resolved from the backend dashboard
   * catalog using {@link DashboardPageKey.BUSINESS_PERFORMANCE_REPORT}.
   * Required by `customDashboardPin.toggleSection(...)` to attach pinned
   * sections to the user's mixed (My Custom) dashboard. Stays `null` when
   * the catalog has no entry — in that case the pin button silently
   * no-ops, so the rest of the page is unaffected.
   */
  readonly effectiveCatalogPageId = signal<number | null>(null);

  /** Backend dashboard-catalog key used to resolve `effectiveCatalogPageId`. */
  readonly PAGE_KEY = DashboardPageKey.BUSINESS_PERFORMANCE_REPORT;

  selectedProductId = signal<string | null>(this.apiService.selectedProductId());
  productConfig = computed(() => {
    const productId =
      this.selectedProductId() ?? this.apiService.selectedProductId();
    const productName = productId
      ? this.productConfigService.getProductName(productId)
      : 'denefits';
    return this.productConfigService.getProductConfig(productName);
  });

  currencyOptions: DropdownOption[] = CURRENCY_DROPDOWN_OPTIONS;

  selectedCurrency = signal<string>('1');

  selectedOption = computed(() => {
    return this.currencyOptions.find(opt => opt.value === this.selectedCurrency());
  });

  selectedCurrencyInfo = computed(() => getCurrencyInfoByCocuId(this.selectedCurrency()));

  dateRange: DateRangePickerOutput = {
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    previous_start_date: new Date(new Date().setDate(new Date().getDate() - 60)).toISOString().split('T')[0],
    previous_end_date: new Date(new Date().setDate(new Date().getDate() - 31)).toISOString().split('T')[0],
    unit: 'day',
    comparison_type: 'previous-period'
  };

  constructor() {
    // React to global product selection changes
    effect(() => {
      const globalProductId = this.apiService.selectedProductId();
      if (globalProductId) {
        untracked(() => {
          this.selectedProductId.set(globalProductId);
        });
      }
    });

    // ─── Resolve backend catalog `pages.id` for "Pin to Custom Dashboard"
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      const activeProductPid = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    // ─── Hydrate which sections are already pinned on the user's mixed
    // (My Custom) dashboard so the green check / "Added" pill renders
    // correctly after a page refresh. Re-runs whenever the catalog page
    // id flips.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<BusinessPerformanceReportSectionId>();
          keys.forEach((k) => {
            const mapped =
              this.WIDGET_KEY_TO_SECTION.get(k) ??
              this.WIDGET_KEY_TO_SECTION.get(this._stripProductPrefix(k));
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(BusinessPerformanceReportSectionId).includes(
                k as BusinessPerformanceReportSectionId,
            )) {
              pinned.add(k as BusinessPerformanceReportSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // ─── Pre-resolve catalog `widgets.id` per section so per-click pins
    // can skip the catalog round-trip (mirrors the Campaign Ads / Social
    // Platform pattern). When the resolved map has an entry for the
    // clicked section, `addToCustomDashboard` forwards the numeric
    // `widgets.id` straight to {@link CustomDashboardPinService.toggleLayoutItem}
    // (one POST/PATCH per click — never the whole page). Sections whose
    // id isn't in the map fall back to the existing `toggleSection`
    // flow so the page's click-to-pin behaviour stays preserved.
    effect(() => {
      const pageId = this.effectiveCatalogPageId();
      const productId = resolveActiveCatalogProductId(this.apiService);
      if (pageId == null || productId == null) {
        untracked(() => this.pinnableWidgetIds.set(new Map()));
        return;
      }
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<BusinessPerformanceReportSectionId, number>();
            for (const sec of Object.values(BusinessPerformanceReportSectionId)) {
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  ngOnInit(): void {
    // Initialization logic here
    this._initLayout();
  }

  rangeValues(event: any) {
   
  }

  private cdr = inject(ChangeDetectorRef);

  onDateRangeChange(event: DateRangePickerOutput) {
    
    // event contains: start_date, end_date, previous_start_date, previous_end_date, unit
    
    // Example usage:
    this.dateRange = event;
    this.cdr.detectChanges();
    
    // Use the data for API calls or other logic
    if (this.dateRange.previous_start_date && this.dateRange.previous_end_date) {
     
    } else {
     
    }
  }

  // ─── Customize Layout (additive — does not affect existing data flow) ──
  readonly SectionId = BusinessPerformanceReportSectionId;
  private readonly SECTIONS = BUSINESS_PERFORMANCE_REPORT_SECTION_DEF;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_business_performance_report_layout_v2';

  /**
   * Default col-span per section — preserves the historical layout where
   * every section was full-width, except the four `business-state-stats`
   * sub-cards which originally rendered in a 2-column grid (so they each
   * default to 6/12 here, sitting two-per-row on lg+ exactly as before).
   */
  private readonly DEFAULT_COL_SPAN: Record<BusinessPerformanceReportSectionId, number> = {
    [BusinessPerformanceReportSectionId.BUSINESSES_REGISTERED]:    12,
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVE]:     6,
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_INACTIVE]:   6,
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVATED]:  6,
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_REVIVED]:    6,
    [BusinessPerformanceReportSectionId.BUSINESS_STAGE]:           12,
    [BusinessPerformanceReportSectionId.BUSINESS_HIGHLIGHTS]:      12,
    [BusinessPerformanceReportSectionId.TRENDING_INDUSTRIES]:      12,
    [BusinessPerformanceReportSectionId.BUSINESS_STATEWISE]:       12,
    [BusinessPerformanceReportSectionId.BUSINESS_PHASE]:           12,
    [BusinessPerformanceReportSectionId.ACTIVE_BUSINESSES]:        12,
    [BusinessPerformanceReportSectionId.BUSINESS_COHORT_GROUP]:    12,
  };

  /**
   * Maps each frontend {@link BusinessPerformanceReportSectionId} to the
   * canonical (product-agnostic) backend `widget_key` suffix from the
   * `visionboard-business` catalog. Values intentionally omit the
   * 2-char product prefix (`de_`, `cr_`, …) — same pattern as Monthly
   * Report 2 / Social Platform:
   *
   *   Denefits  → `de_vb_registered_today`   (pages.id = 41)
   *   Credee    → `cr_vb_registered_today`   (pages.id = 43)
   *
   * {@link CustomDashboardPinService} and
   * {@link CustomizeLayoutBatchService} resolve the correct numeric
   * `widgets.id` per active product via exact match on the canonical
   * key or the `widget_key.endsWith('_' + key)` suffix rule.
   */
  private readonly SECTION_WIDGET_KEYS: Record<BusinessPerformanceReportSectionId, string> = {
    [BusinessPerformanceReportSectionId.BUSINESSES_REGISTERED]:    'vb_registered_today',
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVE]:    'vb_active_business',
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_INACTIVE]:  'vb_inactive_business',
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_ACTIVATED]: 'vb_business_activated',
    [BusinessPerformanceReportSectionId.BUSINESS_STATE_REVIVED]:   'vb_business_revived',
    [BusinessPerformanceReportSectionId.BUSINESS_STAGE]:           'vb_business_stage',
    [BusinessPerformanceReportSectionId.BUSINESS_HIGHLIGHTS]:      'vb_business_highlights',
    [BusinessPerformanceReportSectionId.TRENDING_INDUSTRIES]:      'vb_trending_industries',
    [BusinessPerformanceReportSectionId.BUSINESS_STATEWISE]:       'vb_business_states',
    [BusinessPerformanceReportSectionId.BUSINESS_PHASE]:           'vb_business_phase',
    [BusinessPerformanceReportSectionId.ACTIVE_BUSINESSES]:        'vb_active_businesses_timeline',
    [BusinessPerformanceReportSectionId.BUSINESS_COHORT_GROUP]:    'vb_cohort_group_activity',
  };

  /** Reverse lookup: canonical / raw backend `widget_key` → section id. */
  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, BusinessPerformanceReportSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        BusinessPerformanceReportSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<BusinessPerformanceReportSectionId, SectionLayoutItem>>(
    {} as Record<BusinessPerformanceReportSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | BusinessPerformanceReportSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12:
      default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: BusinessPerformanceReportSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(this.DEFAULT_COL_SPAN[s.id] ?? 12),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<BusinessPerformanceReportSectionId, SectionLayoutItem> =
      {} as Record<BusinessPerformanceReportSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence — see {@link _persistLayoutToBackend}.
    // The local state above is unchanged regardless of whether the
    // network call succeeds, so existing functionality / UI is preserved.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via the shared {@link CustomizeLayoutBatchService} —
   * same flow Campaign Ads Stats / Monthly Report 1 / 2 use.
   *
   * Two paths so the API call always fires regardless of whether
   * the reactive catalog effect in the constructor has hydrated:
   *
   *   • **Fast path** — `effectiveCatalogPageId()` already cached
   *     (catalog GET completed before the user clicked Apply). Goes
   *     straight to the `…ByKeys` service variant which forkJoins
   *     the catalog + per-page dashboard load and resolves widget
   *     ids from whichever responds with usable data.
   *
   *   • **Slow / fallback path** — pageId not yet cached (very
   *     first Apply on a fresh load before `selectedProductId`
   *     signal fired, or catalog GET still in flight). Triggers
   *     `getCatalogProducts()` on demand, resolves the page id
   *     synchronously inside the click, caches it for next click,
   *     then hands off to the same service variant. The catalog
   *     request is normally cached by the layout service, so this
   *     is essentially free on subsequent clicks.
   *
   * Errors are silently swallowed by the service (mirrors the
   * MR1/MR2/Campaign Ads behaviour) — local state already applied.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  /**
   * Diagnostic warning printed when {@link findCatalogPageId} can't
   * resolve a `pages.id` for this report. Dumps the list of page
   * names the catalog actually advertises for the active product so
   * the developer can either:
   *
   *   • Align the backend page name with the configured hints in
   *     `CATALOG_PAGE_HINTS[BUSINESS_PERFORMANCE_REPORT]`, or
   *   • Add a new hint variant that matches the backend name.
   *
   * Stays scoped to this component (does not touch shared helpers)
   * so the existing behaviour of every other dashboard is preserved.
   */
  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
            (p: any) => String(p?.id) === String(productId),
          )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[BusinessPerformanceReport] No catalog page found for ` +
        `"${this.PAGE_KEY}" (active product=${productId}). ` +
        `Catalog advertised pages for this product: ` +
        `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
        `If the correct page is in the list above, add a matching ` +
        `substring to CATALOG_PAGE_HINTS[BUSINESS_PERFORMANCE_REPORT] ` +
        `in dashboard-embed.helpers.ts. If the list is empty, the ` +
        `backend has no catalog page for this report yet.`,
    );
  }

  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: BusinessPerformanceReportSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: BusinessPerformanceReportSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: BusinessPerformanceReportSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'col-span-4';
      case 6:  return 'col-span-6';
      case 8:  return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4:  return 'Small';
      case 6:  return 'Medium';
      case 8:  return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
/* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<BusinessPerformanceReportSectionId, SectionLayoutItem> =
      {} as Record<BusinessPerformanceReportSectionId, SectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<BusinessPerformanceReportSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  /**
   * Strips a leading 2-char product prefix (`de_`, `cr_`, …) so
   * pinned-widget hydration works regardless of which product is active.
   * Mirrors the helper in `custom-dashboard-pin.service.ts`.
   */
  private _stripProductPrefix(widgetKey: string): string {
    const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
    return match ? match[1] : widgetKey;
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  // ─── "Pin to Custom Dashboard" (API-backed, mirrors Campaign Ads / SEO) ─
  /** Section ids that are currently pinned on the user's mixed dashboard. */
  readonly pinnedSections = signal<Set<BusinessPerformanceReportSectionId>>(new Set());
  /** Sections with an in-flight pin/unpin request. Drives the spinner. */
  readonly pendingPinSections = signal<Set<BusinessPerformanceReportSectionId>>(new Set());

  /**
   * Catalog `widgets.id` resolved per section, keyed by canonical
   * {@link BusinessPerformanceReportSectionId}. Populated by the
   * constructor effect once the catalog products + page id resolve,
   * and consumed by {@link addToCustomDashboard} so per-click pins
   * use the pre-resolved id with
   * {@link CustomDashboardPinService.toggleLayoutItem} — one POST/PATCH
   * per click; never the whole page. Sections whose id isn't yet in
   * the map fall back to {@link CustomDashboardPinService.toggleSection}
   * so the page's click-to-pin behaviour is preserved while the
   * catalog is still loading.
   */
  readonly pinnableWidgetIds = signal<Map<BusinessPerformanceReportSectionId, number>>(
    new Map(),
  );

  isAddedToCustomDashboard(
    sec: { id: BusinessPerformanceReportSectionId } | BusinessPerformanceReportSectionId,
  ): boolean {
    // Accept both the full section layout item (this template passes `sec`)
    // and the raw section id. Normalize to a string key BEFORE
    // `Set.has(...)` — otherwise `pinnedSections().has(<object>)` would
    // always return false against the string-keyed Set and the green
    // "Added" pill / tooltip would never light up.
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(
    sec: { id: BusinessPerformanceReportSectionId } | BusinessPerformanceReportSectionId,
  ): boolean {
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    return this.pendingPinSections().has(sectionId);
  }

  /**
   * Toggles whether `sec` is pinned on the user's mixed (My Custom)
   * dashboard. Reuses the shared {@link CustomDashboardPinService} flow —
   * same path Campaign Ads / Social Platform / SEO Stats use — so this
   * page does not introduce any new backend contract.
   *
   * Two paths so the click always does the right thing regardless of
   * whether the reactive catalog effect in the constructor has
   * hydrated yet:
   *   • **Fast path** — `pinnableWidgetIds()` already has the catalog
   *     `widgets.id` for this section. We forward the numeric id
   *     directly to {@link CustomDashboardPinService.toggleLayoutItem}
   *     (mirrors Social Platform / Monthly Report 1 / 2 — one
   *     POST/PATCH per click, never the whole page).
   *   • **Fallback path** — catalog `widgets.id` not yet resolved
   *     (rare; only in the brief window after first page load).
   *     Hands off to {@link CustomDashboardPinService.toggleSection}
   *     which performs the catalog lookup on click, preserving the
   *     page's pre-existing click-to-pin behaviour 1:1.
   *
   * When the catalog page id itself is unresolved (e.g. backend
   * doesn't expose this report yet) the call early-returns and the
   * UI stays in a stable state.
   */
  addToCustomDashboard(
    sec: { id: BusinessPerformanceReportSectionId } | BusinessPerformanceReportSectionId,
  ): void {
    const sectionId = typeof sec === 'object' && sec !== null ? sec.id : sec;
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    // Hands the click off to {@link CustomDashboardPinService} — the
    // EXACT same service Monthly Report 1 / 2 use via
    // `_customDashboardPin.toggleLayoutItem(...)`. When a numeric
    // `widgets.id` is already cached we forward it straight to
    // {@link CustomDashboardPinService.toggleLayoutItem} (mirrors MR1);
    // otherwise we fall back to {@link CustomDashboardPinService.toggleSection}
    // which performs the catalog lookup itself before firing the same
    // mixed-dashboard API (PATCH /dashboards/:id/widgets/:wid/visibility
    // when the mixed dashboard already exists, otherwise POST /dashboards
    // to seed it the first time).
    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    // Fast path — catalog `pages.id` already cached by the constructor
    // effect. Fires the API immediately, no additional round-trips.
    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    // Fallback / slow path — pageId not yet resolved (catalog GET still
    // in flight, or the constructor effect's first run hasn't completed
    // yet). Resolve the catalog on-click so the API still fires,
    // mirroring the pattern used by `_persistLayoutToBackend` for the
    // Customize Layout Apply button. The catalog response is normally
    // cached by the layout service, so subsequent clicks hit the fast
    // path above.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        // Cache so the fast path kicks in on the next click and the
        // existing hydration effect for "Added" pills stays consistent.
        this.effectiveCatalogPageId.set(resolvedPageId);

        // Resolve `widgets.id` map for this page so this click can use
        // the fast `toggleLayoutItem` variant (one POST/PATCH per
        // click — never the whole page). When the map already has an
        // entry from a prior effect run, this is essentially free.
        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(BusinessPerformanceReportSectionId)) {
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }

}
